import { useEffect, useRef } from "react";
import type { TripLeg } from "@poi-planner/shared";
import { useLegStore } from "../../store/legStore";
import { useTripStore } from "../../store/tripStore";
import type { DateRangePreview } from "../../store/calendarDndStore";
import {
  resizeDateRange,
  type RangeEdge,
} from "../../utils/calendarRangeResize";
import type { DateRangeResizeTarget } from "./DateRangeResizeHandle";

/**
 * Move/resize behaviour for a leg bar, shared by the week band and the month
 * grid. Both render a leg as a draggable range with two resize edges, and both
 * need the same three things: edge previews, the commit that clamps to the
 * trip's dates, and suppression of the click a drag leaves behind.
 */
export function useLegRangeGesture(leg: TripLeg, isDragging: boolean) {
  const trip = useTripStore((state) => state.trip);
  const updateLeg = useLegStore((state) => state.updateLeg);

  // Suppress the click that follows a drag so moving a bar doesn't also open
  // the editor.
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true;
      return;
    }
    if (!wasDragging.current) return;
    const reset = window.setTimeout(() => {
      wasDragging.current = false;
    }, 0);
    return () => window.clearTimeout(reset);
  }, [isDragging]);

  const resizeForEdge = (edge: RangeEdge, target: DateRangeResizeTarget) =>
    trip
      ? resizeDateRange(
          leg.startDate,
          leg.endDate,
          edge,
          target.date,
          trip.startDate,
          trip.endDate,
        )
      : null;

  const previewForEdge = (
    edge: RangeEdge,
    target: DateRangeResizeTarget,
  ): DateRangePreview => {
    const resized = resizeForEdge(edge, target);
    return {
      startDate: resized?.startDate ?? target.date,
      endDate: resized?.endDate ?? target.date,
      targetDate: target.date,
      label: `${leg.name} ${edge}`,
      invalid: !resized,
      conflict: false,
    };
  };

  const commitEdge = (edge: RangeEdge, target: DateRangeResizeTarget) => {
    if (!trip) return;
    const resized = resizeForEdge(edge, target);
    if (
      !resized ||
      (resized.startDate === leg.startDate && resized.endDate === leg.endDate)
    ) {
      return;
    }
    void updateLeg(trip.id, leg.id, {
      startDate: resized.startDate,
      endDate: resized.endDate,
    });
  };

  return { trip, wasDragging, previewForEdge, commitEdge };
}
